<template>
  <div class="container rtb-container" v-if="auction.id">

    <div class="auction-info-container">
      <div class="auction-info">
        <div class="photo" :style="'background-image: url(' + auctionPhoto() + ')'">
          <div style="position: absolute; top: .5rem; left: .5rem; width: 2rem;"><img :src="tenantLogoUrl(tenantProfile)" style="width: 100%"/></div>
        </div>
        <div class="text">
          <strong class="name">{{ auction.address }}</strong><br/>
          {{ auction.zip }} {{ auction.city }}, {{ auction.state }}<br/>
          APN: {{ auction.metadata.apn || '—' }}<br/>
          <strong class="sub">Auction Period:</strong><br/>
          {{ auction.startDate | prettyDateTime(auction.metadata.timezone) }} &ndash;<br/>
          {{ auction.endDate | prettyDateTime(auction.metadata.timezone) }}<br/>
        </div>
      </div>
      <div class="questions">
        <AgentCard :userId="auction.listingAgent" :tenantSubdomain="auction.tenant.subdomain" />
      </div>
    </div>

    <!-- verify that auction status is open -->
    <template v-if="auction.status !== auctionStatuses.OPEN.value">
      <div class="rtb align-center">
        <h1>Invite your Buyer to Register to Bid</h1>
        <p>This auction is not open to new registrations. Either the auction is already ongoing or closed.</p>
        <router-link :to="'/auction/' + auction.id"><button>Back to auction page</button></router-link>
      </div>
    </template>

    <template v-else-if="auction.role.role !== auctionRoles.ANONYMOUS.value && auction.role.role !== auctionRoles.SELLING_AGENT.value">
      <div class="rtb align-center">
        <h1>Invite your Buyer to Register to Bid</h1>
        <p>You cannot create invitations since you're already a participant in this auction in some other role, and thus you cannot be acting as a selling agent.</p>
        <router-link :to="'/auction/' + auction.id"><button>Back to auction page</button></router-link>
      </div>
    </template>

    <!-- ok, show the invitation form -->
    <template v-else>

      <div class="rtb">
        <h1>Invite your Buyer to Register to Bid</h1>
        <p>
          If you are an Agent representing a potential buyer, please invite your buyer to
          register to bid in this auction. Only a registered buyer may place bids during the auction.
        </p>
        <p>
          The email address associated with your profile is <strong>{{ this.$store.state.user.email }}</strong>
          and it will be used by the potential buyers to connect to you, if they choose to proceed with you in this auction.
        </p>
        <div v-if="alreadyInvited.length > 0">
          You have already invited earlier:
          <ul class="list-bullets">
            <li v-for="email in alreadyInvited" :key="email">{{ email }}</li>
          </ul>
        </div>
        <form ref="inviteBuyerForm">
          <fieldset>
            <label>Email addresses *</label>
            <input type="email" v-for="(email, index) in inviteBuyerEmails" :key="index" v-model.trim="inviteBuyerEmails[index]" placeholder="Email address"/>
          </fieldset>
        </form>
        <div style="text-align: center;">
          <router-link :to="'/auction/' + auction.id"><button class="btn-invisible">Cancel</button></router-link>
          <button class="wider" @click="sendInvitations" :disabled="!emailFilled">Send</button>
        </div>
      </div>

    </template>

  </div>
</template>

<script>
  import AgentCard from './elements/AgentCard.vue'
  import { auctionRoles, auctionStatuses, auctionInvitationRoles, tenantLogoUrl } from '../utils/PfxUtils.js'

  export default {
    name: 'RegisterToBidInviteBuyer',
    components: {
      AgentCard
    },
    data() {
      return {
        auctionStatuses,
        auctionRoles,
        auctionId: this.$route.params.auctionId,
        auction: { metadata: {} },
        tenantProfile: {},
        inviteBuyerEmails: ['', ''],
        alreadyInvited: []
      }
    },
    computed: {
      emailFilled() {
        return this.inviteBuyerEmails.filter(e => e.length > 0).length > 0
      }
    },
    watch: {
      inviteBuyerEmails() {
        const numberOfEmptyFields = this.inviteBuyerEmails.filter(e => e.length === 0).length
        if (numberOfEmptyFields < 2) { this.inviteBuyerEmails.push('') }
        if (numberOfEmptyFields > 3) { this.inviteBuyerEmails.splice(this.inviteBuyerEmails.findIndex(e => e.length === 0), 1) }
      }
    },
    mounted() {
      this.$store.state.loadingSpinner = true
      this.$PfxBackendApi.getAuction(this.auctionId).then(res => {
        console.log('getAuction res', res)
        this.auction = res.data || {}
        this.$store.state.loadingSpinner = 2

        this.$PfxBackendApi.getAuctionInvitationsByMe(this.auction.tenant.subdomain, this.auction.id).then(res => {
          console.log('getAuctionInvitationsByMe res', res)
          const invitations = res.data || []
          invitations.forEach(invitation => {
            if (invitation.auctionInvitationRole === auctionInvitationRoles.BUYER.value) {
              this.alreadyInvited.push(invitation.email)
            }
          })
        }).catch(err => {
          console.error('getAuctionInvitationsByMe err', err)
        }).then(() => this.$store.state.loadingSpinner--)

        // fetch tenant profile (for logo)
        this.$PfxBackendApi.getTenantPublic(this.auction.tenant.subdomain).then(res => {
          console.log('getTenantPublic res', res)
          this.tenantProfile = res.data || {}
        }).catch(err => {
          console.error('getTenantPublic err', err)
        }).then(() => this.$store.state.loadingSpinner--)

      }).catch(err => {
        console.error('getAuction err', err)
        this.$notify({ type: 'danger', message: 'Cannot fetch auction data' })
        this.$store.state.loadingSpinner = false
      })
    },
    methods: {
      tenantLogoUrl,

      auctionPhoto() {
        if (this.auction.metadata && this.auction.metadata.photos && this.auction.metadata.photos.length > 0) {
          return this.auction.metadata.photos[0]
        } else {
          return require('../assets/placeholder-photos.jpg')
        }
      },

      sendInvitations() {
        if (!this.emailFilled) { return }

        if (this.$refs.inviteBuyerForm && !this.$refs.inviteBuyerForm.checkValidity()) {
          this.$refs.inviteBuyerForm.reportValidity()
          return
        }

        // We use Set to remove duplicates and then spread it back to an array
        const emails = [...new Set(this.inviteBuyerEmails.filter(e => e.length > 0))]

        const that = this
        that.$store.commit('setLoadingSpinner', 0)
        emails.forEach(email => {
          console.log('sending invitations [%s]', email)
          that.$store.commit('setLoadingSpinner', '++')

          that.$PfxBackendApi.createAuctionInvitationForBuyer(that.auction.tenant.subdomain, that.auction.id, email).then(res => {
            console.log('createAuctionInvitationForBuyer res', res)
            this.$notify({ type: 'info', message: 'Invitation sent to: ' + email })

          }).catch(err => {
            console.error('createAuctionInvitationForBuyer error', err)
            this.$notify({ type: 'danger', message: 'Unexpected error during sending the invitation' })
          }).then(() => that.$store.commit('setLoadingSpinner', '--'))
        })
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  .auction-info-container {
    position: relative;
    @include clearfix();

    .auction-info {
      position: relative;
      @include stack();
      @include tablet-landscape-breakpoint {
        @include column(2/3);
      }

      .photo {
        width: 200px;
        height: 150px;
        background-size: cover;
        background-position: center;
        background-color: #aad2e3;
        float: left;
      }
      .text {
        margin-left: calc(200px + 1em);
      }
    }

    .questions {
      @include stack();
      @include tablet-landscape-breakpoint {
        @include column(1/3);
        text-align: right;
      }
    }
  }

  .auction-info-container {
    padding: 1rem 0;
    border-bottom: 1px solid #f3f3f3;

    .auction-info {
      .text {
        .name {
          font-size: 1.25em;
          line-height: 1em;
        }
        .sub {
          display: inline-block;
          padding-top: 0.5em;
        }
      }
    }
  }

  .rtb-container {
    padding-bottom: 5rem;
  }

  .rtb {
    @include center($max-width: 42rem);

    h1 {
      font-weight: 300;
      text-align: center;
      padding: 2rem 0 0;
    }
    h2 {
      small {
        color: #aaa;
      }
    }

    .stepper.no-shadow {
      box-shadow: none;
    }

    .container {
      background-color: #e9e9e9;
      padding: 1rem 2rem;
      margin-bottom: 1rem;

      .top-bar {
        @include clearfix();
        cursor: pointer;

        h2 {
          @include column(7/8);
        }
        .status {
          @include column(1/8);
          text-align: right;
          margin-top: 0.5em;
        }
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/RegisterToBidInviteBuyer.vue